import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { PlusCircle, X, Trash2 } from "lucide-react";
import BulkImport from "./bulk-import";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Configure() {
  const [items, setItems] = useState([
    { label: "Track Number", value: "fingerprint", db: "songs" },
    { label: "Title", value: "title", db: "songs" },
    { label: "Primary Artist", value: "primaryArtist", db: "songs" },
    { label: "Secondary Artist", value: "secondaryArtist", db: "songs" },
    { label: "Lyricist", value: "lyricist", db: "songs" },
    { label: "Composer", value: "composer", db: "songs" },
    { label: "Language", value: "language", db: "songs" },
  ]);

  const [tags, setTags] = useState([]);

  const [inputValue, setInputValue] = useState("");

  const [cards, setCards] = useState([]);

  const [codeStats, setCodeStats] = useState({ isrc: 0, upc: 0 });
  const fileInputRef = useRef(null);

  const fetchCodeStats = async () => {
    try {
      const res = await axios.get("/admin/configure/code-stats");
      if (res.data.ok) {
        setCodeStats(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch code stats", err);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (inputValue) {
      let newTag = { id: "TG" + tags.length, name: inputValue, selected: true };
      let SheetValue = {
        tagId: newTag.id,
        SheetName: "",
        form: [{ db_col: "", xl_col: "" }],
      };
      const updatedTags = tags.map((tag) => ({ ...tag, selected: false }));
      updatedTags.push(newTag);
      setTags(updatedTags);
      setCards([...cards, SheetValue]);
    }
    setInputValue("");
  };

  const getCurrentTagId = () => {
    return tags.find((tag) => tag.selected == true)?.id;
  };

  const onDbColChange = (event, formIndex, cardIndex) => {
    let data = [...cards];
    data[cardIndex].form[formIndex].db_col = event;
    setCards(data);
  };

  const getAllConfigure = () => {
    axios.get("/admin/configure/all-tags").then((response) => {
      let responseData = response.data.data;
      if (responseData) {
        if (responseData.tags) {
          let loadTags = [];

          loadTags = responseData.tags.map((tag) => {
            return {
              id: tag.id,
              name: tag.name,
              selected: tag.selected ? tag.selected : false, // Default to false if not provided
            };
          });

          setTags(loadTags);
        }
        if (responseData.mapper) {
          let loadCard = [];

          loadCard = responseData.mapper.map((card) => {
            return {
              tagId: card.tagId,
              id: card._id,
              SheetName: card.SheetName,
              form: card.form,
            };
          });
          setCards(loadCard);
        }
      }
    });
  };

  useEffect(() => {
    getAllConfigure();
    fetchCodeStats();
  }, []);

  const addCard = () => {
    let object = {
      tagId: getCurrentTagId(),
      SheetName: "",
      form: [{ db_col: "", xl_col: "" }],
    };

    setCards([...cards, object]);
  };

  const addFields = (cardIndex) => {
    let data = [...cards];
    data[cardIndex].form.push({ db_col: "", xl_col: "" });
    setCards(data);
  };

  const handleFormChange = (event, formIndex, cardIndex) => {
    let data = [...cards];
    data[cardIndex].form[formIndex].xl_col = event.target.value;
    setCards(data);
  };

  const handleSheetNameChange = (event, index) => {
    let data = [...cards];
    data[index][event.target.name] = event.target.value;
    setCards(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    console.log(tags, cards);
    let data = { tags: tags, cards: cards };
    const result = await axios.post("/admin/configure/addconfigure", data);
  };

  const handleFileUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post("/admin/configure/upload-codes", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Codes uploaded successfully");
      fetchCodeStats();
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("Upload failed", err);
      alert("Upload failed");
    }
  };

  const removeCards = async (index) => {
    let data = [...cards];
    console.log();
    if (data[index]?.id) {
      let mapperId = data[index]?.id;
      const result = await axios.delete(
        `/admin/configure/mapper?mapperId=${mapperId}`
      );
    }
    data.splice(index, 1);
    setCards(data);
  };

  const removeFields = (formIndex, cardIndex) => {
    let data = [...cards];
    data[cardIndex].form.splice(formIndex, 1);
    setCards(data);
  };

  const updateTagsSelected = (id) => {
    const updatedTags = tags.map((tag) => ({
      ...tag,
      selected: tag.id === id,
    }));

    setTags(updatedTags);
  };

  const handleCloseTag = (removedTag) => {
    // Remove the card associated with the removed tag from the cards state
    const updatedCards = cards.filter((card) => card.tagId !== removedTag);
    console.log(removedTag);
    setCards(updatedCards);

    // Remove the tag from the tags state
    const updatedTags = tags.filter((tag) => tag.id !== removedTag);
    if (
      updatedTags.length >= 1 &&
      updatedTags.every((tag) => tag.selected == false)
    ) {
      updatedTags[0].selected = true;
    }
    setTags(updatedTags);
  };

  return (
    <div className="p-6 h-[calc(100vh-4rem)] flex flex-col gap-6">
      <div className="flex justify-between items-center pb-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Configuration</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your metadata providers and ISRC/UPC pools.
          </p>
        </div>
        <Button onClick={submit}>Save Changes</Button>
      </div>

      <Tabs defaultValue="codes" className="flex flex-col flex-1 min-h-0 gap-6">
        <TabsList className="max-w-min">
          <TabsTrigger value="codes">ISRC & UPC Codes</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="bulk-import">Bulk Import</TabsTrigger>
        </TabsList>

        <TabsContent
          value="providers"
          className="flex-1 flex gap-8 min-h-0 mt-0"
        >
          {/* Left Sidebar: Providers List */}
          <div className="w-64 flex-shrink-0 flex flex-col gap-4">
            <Card className="flex-1 flex flex-col overflow-hidden">
              <div className="p-4 border-b bg-muted/30">
                <h2 className="font-semibold text-sm mb-3">All Providers</h2>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder="New Provider..."
                    className="h-9 bg-background"
                  />
                  <Button
                    size="sm"
                    onClick={handleInputConfirm}
                    disabled={!inputValue}
                    className="h-9 w-9 px-0 flex-shrink-0"
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {tags.map((tag) => (
                  <div
                    key={tag.id}
                    onClick={() => updateTagsSelected(tag.id)}
                    className={`
                      group flex items-center justify-between px-3 py-2.5 rounded-md text-sm cursor-pointer transition-all
                      ${
                        tag.selected
                          ? "bg-secondary text-secondary-foreground"
                          : "hover:bg-muted text-foreground"
                      }
                    `}
                  >
                    <span className="truncate font-medium">{tag.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`
                        h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity
                        ${
                          tag.selected
                            ? "text-secondary-foreground hover:bg-secondary-foreground/20"
                            : "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        }
                      `}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseTag(tag.id);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {tags.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                      <PlusCircle className="h-4 w-4" />
                    </div>
                    <p className="text-xs">Add your first provider</p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Main Content: Active Provider Configuration */}
          <div className="flex-1 overflow-y-auto pr-2">
            {tags.find((t) => t.selected) ? (
              <div className="space-y-6 max-w-4xl mx-auto pb-10">
                <div className="flex flex-col gap-1 mb-4">
                  <h2 className="text-xl font-semibold">
                    {tags.find((t) => t.selected)?.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Configure mapping rules for this provider.
                  </p>
                </div>

                {cards.map((card, cardIndex) => {
                  if (card.tagId === getCurrentTagId()) {
                    return (
                      <Card key={cardIndex} className="overflow-hidden">
                        <div className="bg-muted/30 p-4 border-b flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-muted-foreground">
                              Sheet:
                            </span>
                            <Input
                              value={card.SheetName}
                              onChange={(e) =>
                                handleSheetNameChange(e, cardIndex)
                              }
                              placeholder="e.g. Metadata"
                              className="h-8 w-40 bg-background"
                              name="SheetName"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeCards(cardIndex)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove
                          </Button>
                        </div>

                        <CardContent className="p-0">
                          <div className="grid grid-cols-[1fr_1fr_auto] gap-4 p-3 border-b bg-muted/10 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            <div className="pl-2">Database Field</div>
                            <div>Excel Header</div>
                            <div className="w-8"></div>
                          </div>

                          <div className="divide-y">
                            {card.form.map((form, formIndex) => (
                              <div
                                key={formIndex}
                                className="grid grid-cols-[1fr_1fr_auto] gap-4 p-3 items-center hover:bg-muted/5 transition-colors"
                              >
                                <Select
                                  value={form.db_col}
                                  onValueChange={(value) =>
                                    onDbColChange(value, formIndex, cardIndex)
                                  }
                                >
                                  <SelectTrigger className="h-9 border-transparent hover:border-input focus:ring-0 bg-transparent hover:bg-background transition-all">
                                    <SelectValue placeholder="Select field..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {items.map((item) => (
                                      <SelectItem
                                        key={item.value}
                                        value={item.value}
                                      >
                                        {item.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>

                                <Input
                                  className="h-9 border-transparent hover:border-input focus-visible:ring-0 bg-transparent hover:bg-background transition-all"
                                  value={form.xl_col}
                                  onChange={(e) =>
                                    handleFormChange(e, formIndex, cardIndex)
                                  }
                                  placeholder="Excel Header Name"
                                />

                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                  onClick={() =>
                                    removeFields(formIndex, cardIndex)
                                  }
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>

                          <div className="p-3 bg-muted/10">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addFields(cardIndex)}
                              className="w-full border-dashed text-muted-foreground hover:text-foreground hover:border-primary/50"
                            >
                              <PlusCircle className="h-4 w-4 mr-2" />
                              Add Mapping Field
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  }
                  return null;
                })}

                <Button
                  onClick={addCard}
                  variant="outline"
                  className="w-full py-6 border-dashed"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add New Sheet Config
                </Button>
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center text-muted-foreground p-8 border-2 border-dashed rounded-xl bg-muted/10 m-4">
                <div className="p-4 bg-background rounded-full mb-4">
                  <PlusCircle className="h-8 w-8 text-primary/40" />
                </div>
                <h3 className="font-semibold text-lg mb-1">
                  No Provider Selected
                </h3>
                <p className="text-sm max-w-xs text-center">
                  Select a provider from the list on the left or create a new
                  one to start configuring mapping rules.
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent
          value="codes"
          className="flex flex-col gap-8 max-w-4xl mx-auto w-full pt-4 mt-0"
        >
          <div className="grid grid-cols-2 gap-6">
            <Card className="transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Available ISRCs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{codeStats.isrc}</div>
                <p className="text-xs text-muted-foreground">
                  Unused ISRC codes in pool
                </p>
              </CardContent>
            </Card>
            <Card className="transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Available UPCs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{codeStats.upc}</div>
                <p className="text-xs text-muted-foreground">
                  Unused UPC codes in pool
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="flex flex-col overflow-hidden">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl">Upload New Codes</CardTitle>
              <CardDescription>
                Import ISRC and UPC codes in bulk from a CSV file.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-8 p-8">
              {/* Upload Area */}
              <div className="w-full max-w-md">
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="group relative flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer hover:bg-muted/30 hover:border-primary/50 transition-all duration-300"
                >
                  <div className="p-4 bg-muted/40 rounded-full group-hover:bg-background group-hover:scale-110 transition-all duration-300 mb-2 ring-1 ring-border">
                    <PlusCircle className="h-8 w-8 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="flex flex-col items-center justify-center pt-2 pb-1 text-center">
                    <span className="text-sm font-semibold text-foreground">
                      Click to upload CSV
                    </span>
                    <p className="text-xs text-muted-foreground mt-1 px-4">
                      or drag and drop file here
                    </p>
                  </div>
                </label>
              </div>

              {/* Sample Table with Separator */}
              <div className="w-full max-w-md space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Required CSV Format
                    </span>
                  </div>
                </div>

                <div className="rounded-lg border bg-muted/10 overflow-hidden">
                  <Table>
                    <TableHeader className="bg-muted/30">
                      <TableRow>
                        <TableHead className="w-[60%] pl-4">Code</TableHead>
                        <TableHead>Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="hover:bg-muted/5">
                        <TableCell className="font-mono text-xs pl-4">
                          USA2P1234567
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="text-[10px] font-normal"
                          >
                            ISRC
                          </Badge>
                        </TableCell>
                      </TableRow>
                      <TableRow className="hover:bg-muted/5">
                        <TableCell className="font-mono text-xs pl-4">
                          123456789012
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="text-[10px] font-normal"
                          >
                            UPC
                          </Badge>
                        </TableCell>
                      </TableRow>
                      <TableRow className="border-0 hover:bg-muted/5">
                        <TableCell className="font-mono text-xs pl-4">
                          USA2P1234568
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="text-[10px] font-normal"
                          >
                            ISRC
                          </Badge>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent
          value="bulk-import"
          className="flex-1 min-h-0 overflow-y-auto mt-0 pr-1"
        >
          <BulkImport />
        </TabsContent>
      </Tabs>
    </div>
  );
}
